import { getToken } from './BearerToken';
import { invokeStream } from './AgentCoreStream';

/**
 * Chat agent AgentCore Runtime client.
 *
 * Single-responsibility: POST to the Bedrock AgentCore Runtime HTTPS
 * invocations endpoint, parse the Server-Sent Events response stream, and
 * forward each decoded ServerEvent to a registered listener. Knows nothing
 * about React or the chat widget's internal state.
 *
 * Auth: Cognito JWT is sent as `Authorization: Bearer <token>`. The
 * AgentCore-side authorizer verifies it.
 *
 * WHERE THAT TOKEN COMES FROM IS THE CALLER'S TO DECIDE. The dashboard's
 * own session token (localStorage `SESSION_TOKEN`) is the default, because
 * that is the only surface this file had when it was written. The DRIVER
 * PORTAL is a separate surface with a separate store: a dispatcher's
 * dashboard id token is kept under the portal's own key, and the portal
 * deliberately never reads the admin one (see Services/PortalApi — its axios
 * instance strips any `Authorization` it did not set itself). So the token is
 * an INJECTED GETTER rather than a hardwired import: one service, two
 * surfaces, and neither of them reaching into the other's storage.
 *
 * The getter may be async, which is what lets the portal hand over
 * `ensurePortalToken` — a getter that renews a nearly-expired id token
 * before the invocation goes out. A dispatcher keeps the board open all
 * shift; without that, the first question after lunch would be a 401 from
 * the Runtime's authorizer.
 *
 * THE TOKEN IS THE WHOLE IDENTITY. The agent takes `actor_id` (which scopes
 * its long-term memory) and every tool's permission claims from this token
 * and from nothing else — never from the request body. A getter that
 * returned a shared or constant token would therefore merge two people's
 * remembered context, so `getAuthToken` must always resolve to the signed-in
 * person's own token.
 *
 * Session: the runtime requires a session id of >=33 chars per invocation
 * via `X-Amzn-Bedrock-AgentCore-Runtime-Session-Id`. The caller supplies
 * one; continuity across turns is what keeps the agent's memory warm.
 *
 * Event shapes are the same `ServerEvent` union the server emits; keep the
 * two in sync when adding new events.
 */

// The endpoint, the bearer header and the SSE frame parsing now live in
// `AgentCoreStream`, shared with the booking AI overview — which became the
// second consumer of this transport and must not carry a second copy of a
// reader that has already learned about trailing frames.
//
// What stayed HERE is everything specific to the CHAT: the injected token
// getter below, the session continuity that keeps the agent's memory warm,
// the status transitions the widget renders, and the cancel-before-send
// rule.

/**
 * What `sendMessage` / `sendAnswer` resolve to, so the caller can tell a
 * stream that RAN OUT from one it cancelled itself.
 *
 * It matters because the two need opposite handling. `ended` means the HTTP
 * body closed — and a body that closed without a `turn.complete` event is a
 * DROPPED stream, which on a phone is the common case: the answer on screen
 * is a fragment and the turn will never finish on its own. `aborted` means
 * we closed it, so whoever closed it already knows what to do.
 *
 * Deliberately an object and not a bare string: a mocked service resolving
 * `undefined` (every existing spec) reads as neither, which keeps those
 * specs' behaviour exactly as it was.
 */
export const STREAM_ENDED = { ended: true };
export const STREAM_ABORTED = { aborted: true };

export default class ChatAgentService {
  constructor({ onEvent, onStatusChange, getAuthToken } = {}) {
    this.onEvent = onEvent ?? (() => {});
    this.onStatusChange = onStatusChange ?? (() => {});
    // The dashboard's own token by default; the portal injects its own (see
    // the note on auth above). Never both: whichever is injected is the only
    // one this service reads.
    this.getAuthToken = getAuthToken ?? getToken;
    this.abortController = null;
  }

  /** Send a user-typed message. */
  async sendMessage(text, sessionId) {
    return this.#invoke({ prompt: text }, sessionId);
  }

  /** Send a structured answer to a previously-asked question. */
  async sendAnswer(questionId, value, sessionId) {
    return this.#invoke({ answer: { questionId, value } }, sessionId);
  }

  /** Abort the in-flight fetch, if any. */
  cancel() {
    if (this.abortController) {
      this.abortController.abort();
      this.abortController = null;
    }
  }

  async #invoke(body, sessionId) {
    // `await`ed because the getter may renew first (the portal's does).
    const token = await this.getAuthToken();
    if (!token) {
      this.onStatusChange('error');
      throw new Error('No bearer token available — cannot invoke chat agent');
    }
    if (!sessionId || sessionId.length < 33) {
      this.onStatusChange('error');
      throw new Error('AgentCore runtime requires a session id of at least 33 characters');
    }

    // Cancel any still-running invocation before starting a new one; the
    // runtime does not support concurrent invocations on one session.
    this.cancel();
    this.abortController = new AbortController();
    const { signal } = this.abortController;

    this.onStatusChange('streaming');

    try {
      const { aborted } = await invokeStream({
        body,
        sessionId,
        signal,
        token,
        onEvent: (event) => this.onEvent(event),
      });
      if (aborted) {
        this.onStatusChange('idle');
        return STREAM_ABORTED;
      }
    } catch (err) {
      this.onStatusChange('error');
      throw err;
    } finally {
      this.abortController = null;
    }

    this.onStatusChange('idle');
    // The body closed. Whether the TURN finished is a different question,
    // answered by the events that came down it — see useChatAgent.
    return STREAM_ENDED;
  }
}
